/**
 * Helpers de la edición masiva por Excel de Clientes y Proveedores
 * (Components/EdicionMasivaEntidad.js). Estilos y criterios copiados de
 * Pages/Panel/Insumos/EdicionMasivaInsumos, que se deja como está.
 *
 * Cada columna se describe con { col, campo, tipo, ... }:
 *   texto | mayusculas | rfc | telefono | digitos | correo | correos | numero | bool |
 *   enum (labels) | referencia (catalogo) | direccion (campo de la dirección)
 *
 * La regla para decidir si una celda cambió: primero se compara contra lo que se exportó;
 * sólo si es distinta se valida y se convierte. Así un dato viejo que hoy no pasaría la
 * validación no bloquea el renglón cuando el usuario ni lo tocó.
 */
import * as XLSX from "xlsx-js-style";

export const RFCS_GENERICOS = ["XAXX010101000", "XEXX010101000"];

// Misma regla `rfc` de Components/FormItem.js.
export const PATRON_RFC = /^(([ÑA-Z|ña-z|&]{3}|[A-Z|a-z]{4})\d{2}((0[1-9]|1[012])(0[1-9]|1\d|2[0-8])|(0[13456789]|1[012])(29|30)|(0[13578]|1[02])31)(\w{2})([A|a|0-9]{1}))$|^(([ÑA-Z|ña-z|&]{3}|[A-Z|a-z]{4})([02468][048]|[13579][26])0229)(\w{2})([A|a|0-9]{1})$/;

// Misma regla `pattern` de Components/FormItem.js.
export const PATRON_TEXTO = /^[A-Za-z0-9\r\n. ,"'´`“”#°ºªáÁéÉíÍóÓúÚüÜñÑ@_=/*+%&()!-:;\\</>[\]]*$/u;

export const PATRON_CORREO = /^[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+$/;

export const boolStr = (v) => (v ? "SI" : "NO");
export const boolVal = (v) => {
  const s = String(v).trim().toLowerCase();
  if (s === "si" || s === "sí") return true;
  if (s === "no") return false;
  return null;
};
export const parseNumero = (val) => Number(String(val).replace(/\$\s?|(,*)/g, ""));

const texto = (v) => (v === null || v === undefined ? "" : String(v).trim());

// Mayúsculas, sin acentos y con espacios colapsados, para comparar nombres de catálogo.
export const normalizar = (v) =>
  texto(v)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ");

// "601 - General de Ley..." -> "601"
const claveDeEtiqueta = (v) => normalizar(texto(v).split(" - ")[0]);

const mismaClave = (a, b) => {
  if (a === b) return true;
  return /^\d+$/.test(a) && /^\d+$/.test(b) && Number(a) === Number(b);
};

export const aclararColor = (hex, factor) => {
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const mix = (c) => Math.round(c + (255 - c) * factor);
  const toHex = (c) => c.toString(16).padStart(2, "0").toUpperCase();
  return `${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

const BORDE_FINO = {
  top: { style: "thin", color: { rgb: "C0C0C0" } },
  bottom: { style: "thin", color: { rgb: "C0C0C0" } },
  left: { style: "thin", color: { rgb: "C0C0C0" } },
  right: { style: "thin", color: { rgb: "C0C0C0" } },
};
export const estiloBannerGrupo = (color) => ({
  fill: { fgColor: { rgb: color } },
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center" },
  border: BORDE_FINO,
});
export const estiloEncabezadoColumna = (color) => ({
  fill: { fgColor: { rgb: aclararColor(color, 0.75) } },
  font: { bold: true, sz: 10, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: BORDE_FINO,
});

/**
 * Busca un valor escrito en el Excel dentro de un catálogo { lista, etiqueta, clave? }.
 * Acepta la etiqueta completa o, si el catálogo tiene clave, sólo la clave ("601", "G03").
 */
export const buscarEnCatalogo = (catalogo, valor) => {
  const buscado = normalizar(valor);
  const lista = catalogo?.lista || [];
  let encontrados = lista.filter((item) => normalizar(catalogo.etiqueta(item)) === buscado);
  if (!encontrados.length && catalogo?.clave) {
    const claveBuscada = claveDeEtiqueta(valor);
    encontrados = lista.filter((item) => mismaClave(normalizar(catalogo.clave(item)), claveBuscada));
  }
  if (!encontrados.length) return { error: "no existe en el catálogo" };
  if (encontrados.length > 1) return { error: "hay más de un registro con ese nombre" };
  return { item: encontrados[0] };
};

const etiquetaDeId = (catalogo, id) => {
  if (!id || !catalogo) return "";
  const item = catalogo.lista.find((x) => String(x._id) === String(id));
  return item ? catalogo.etiqueta(item) : "";
};

const buscarEnum = (labels, valor) => {
  const buscado = normalizar(valor);
  const claveBuscada = claveDeEtiqueta(valor);
  const entrada = Object.entries(labels).find(
    ([, label]) => normalizar(label) === buscado || mismaClave(claveDeEtiqueta(label), claveBuscada)
  );
  return entrada ? Number(entrada[0]) : null;
};

const soloDigitos = (v) => texto(v).replace(/[\s\-().]/g, "");

// CP de 4 dígitos que Excel dejó sin el cero inicial.
export const normalizarCp = (v) => {
  const cp = texto(v);
  return /^\d{4}$/.test(cp) ? `0${cp}` : cp;
};

/** Valor que se escribe en el Excel para la columna. */
export const valorExportado = (spec, entidad, ctx) => {
  const actual = spec.tipo === "direccion" ? ctx.direccionDe(entidad)[spec.campo] : entidad[spec.campo];
  switch (spec.tipo) {
    case "bool":
      return boolStr(actual);
    case "enum":
      return spec.labels[actual] || "";
    case "referencia":
      return etiquetaDeId(ctx.catalogos[spec.catalogo], actual);
    case "correos":
      return (actual || []).map((c) => c?.correo).filter(Boolean).join("; ");
    case "numero":
      return actual ?? "";
    default:
      // Teléfonos, cuentas y CP van como texto para que Excel no los convierta a notación
      // científica ni les quite ceros.
      return actual === null || actual === undefined ? "" : String(actual);
  }
};

/**
 * Convierte la celda al valor que se manda al backend.
 * Devuelve { valor, display } o { error }.
 */
const convertir = (spec, celda, ctx) => {
  const val = texto(celda);
  switch (spec.tipo) {
    case "texto":
    case "mayusculas":
    case "rfc": {
      const valor = spec.tipo === "texto" ? val : val.toUpperCase();
      if (spec.max && valor.length > spec.max) return { error: `máximo ${spec.max} caracteres` };
      if (spec.patron && valor && !PATRON_TEXTO.test(valor)) return { error: "contiene caracteres no aceptados" };
      if (spec.tipo === "rfc") {
        if (!valor) return { error: "no puede quedar vacío" };
        if (!PATRON_RFC.test(valor)) return { error: `"${valor}" no es un RFC válido` };
      }
      return { valor, display: valor };
    }
    case "telefono":
    case "digitos": {
      const digitos = spec.tipo === "telefono" ? soloDigitos(val) : val.replace(/\s/g, "");
      if (!digitos) return { valor: null, display: "" };
      if (!/^\d+$/.test(digitos)) return { error: `"${val}" debe contener sólo dígitos` };
      if (spec.tipo === "telefono" && digitos.length > 15) return { error: "máximo 15 dígitos" };
      if (spec.longitud && digitos.length !== spec.longitud) return { error: `debe tener ${spec.longitud} dígitos` };
      if (spec.max && digitos.length > spec.max) return { error: `máximo ${spec.max} dígitos` };
      return { valor: digitos, display: digitos };
    }
    case "correo":
      if (val && !PATRON_CORREO.test(val)) return { error: `"${val}" no es un correo válido` };
      return { valor: val, display: val };
    case "correos": {
      const correos = val.split(/[;,]/).map(texto).filter(Boolean);
      const invalido = correos.find((c) => !PATRON_CORREO.test(c));
      if (invalido) return { error: `"${invalido}" no es un correo válido` };
      return { valor: correos, display: correos.join("; ") };
    }
    case "numero": {
      if (!val) {
        if (spec.requerido) return { error: "no puede quedar vacío" };
        return { valor: null, display: "" };
      }
      const num = parseNumero(val);
      if (!Number.isFinite(num) || num < 0) return { error: `"${val}" no es un número válido` };
      return { valor: num, display: String(num) };
    }
    case "bool": {
      const bv = boolVal(val);
      if (bv === null) return { error: `valor inválido "${val}" (use SI o NO)` };
      return { valor: bv, display: boolStr(bv) };
    }
    case "enum": {
      if (!val) {
        if (spec.requerido) return { error: "no puede quedar vacío" };
        return { valor: null, display: "" };
      }
      const clave = buscarEnum(spec.labels, val);
      if (clave === null) return { error: `"${val}" inválido (use ${Object.values(spec.labels).join(", ")})` };
      return { valor: clave, display: spec.labels[clave] };
    }
    case "referencia": {
      if (!val) {
        if (spec.requerido) return { error: "no puede quedar vacío" };
        return { valor: null, display: "" };
      }
      const catalogo = ctx.catalogos[spec.catalogo];
      const { item, error } = buscarEnCatalogo(catalogo, val);
      if (error) return { error: `"${val}" ${error}` };
      return { valor: String(item._id), display: catalogo.etiqueta(item) };
    }
    case "direccion": {
      const valor = spec.campo === "cp" ? normalizarCp(val) : val;
      if (spec.campo === "cp" && valor && !/^\d{5}$/.test(valor)) return { error: `"${val}" debe tener 5 dígitos` };
      return { valor, display: valor };
    }
    default:
      return { error: "tipo de columna desconocido" };
  }
};

// ¿El valor convertido es el mismo que ya tiene el registro?
const mismoValor = (spec, valor, entidad, ctx) => {
  const actual = spec.tipo === "direccion" ? ctx.direccionDe(entidad)[spec.campo] : entidad[spec.campo];
  switch (spec.tipo) {
    case "referencia":
      return String(valor || "") === String(actual || "");
    case "bool":
      return valor === !!actual;
    case "numero":
    case "enum":
      return (valor ?? null) === (actual ?? null);
    case "telefono":
    case "digitos":
      return String(valor ?? "") === String(actual ?? "");
    case "correos":
      return valor.join(";") === (actual || []).map((c) => c?.correo).join(";");
    case "mayusculas":
    case "rfc":
      return valor === texto(actual).toUpperCase();
    default:
      return valor === texto(actual);
  }
};

/**
 * Evalúa una celda contra el registro actual.
 * Devuelve { cambio: false, display }, { cambio: true, valor, display } o { error }.
 */
export const evaluarCelda = (spec, celda, entidad, ctx) => {
  const exportado = valorExportado(spec, entidad, ctx);
  if (texto(celda) === texto(exportado)) return { cambio: false, display: texto(exportado) };

  const r = convertir(spec, celda, ctx);
  if (r.error) return { error: `${spec.col}: ${r.error}` };
  if (mismoValor(spec, r.valor, entidad, ctx)) return { cambio: false, display: r.display };
  return { cambio: true, valor: r.valor, display: r.display };
};

/**
 * Revisa la identidad final (RFC + nombres) de un renglón con cambios de identidad. Misma
 * regla que el backend: `persona` sólo se recalcula si el RFC cambia (12 = moral, 13 = física,
 * los genéricos conservan la que había).
 */
export const erroresIdentidad = (entidad, cambios) => {
  const campos = ["rfc", "razonSocial", "nombre", "apellidoPaterno", "apellidoMaterno"];
  if (!campos.some((c) => c in cambios)) return [];

  const final = {};
  campos.forEach((c) => {
    final[c] = c in cambios ? cambios[c] : texto(entidad[c]).toUpperCase();
  });

  let persona = !!entidad.persona;
  if ("rfc" in cambios && !RFCS_GENERICOS.includes(final.rfc)) persona = final.rfc.length === 12;

  const errores = [];
  if (persona && !final.razonSocial) errores.push("Razon Social: es obligatoria para persona moral");
  if (!persona && !final.nombre) errores.push("Nombre: es obligatorio para persona física");
  return errores;
};

/**
 * Arma y descarga el libro: hoja principal (fila 1 = grupos combinados y coloreados, fila 2 =
 * columnas), Instrucciones, Ejemplos y una hoja por catálogo.
 *
 * @param {Object} args
 * @param {String} args.hoja             nombre de la hoja principal
 * @param {Array}  args.columnas         [{ col, grupo: { key, label, color } }] en orden
 * @param {Array}  args.filas            arreglos alineados con `columnas`
 * @param {Array}  args.instrucciones    renglones de texto
 * @param {Object} args.ejemplos         col -> [ejemplo, ...]
 * @param {Array}  args.catalogos        [{ nombre, encabezados, filas }]
 * @param {String} args.nombreArchivo
 */
export const descargarLibroEdicionMasiva = ({
  hoja,
  columnas,
  filas,
  instrucciones,
  ejemplos,
  catalogos,
  nombreArchivo,
}) => {
  const filaGrupos = columnas.map(({ grupo }) => grupo.label);
  const filaColumnas = columnas.map(({ col }) => col);
  const ws = XLSX.utils.aoa_to_sheet([filaGrupos, filaColumnas, ...filas]);

  const merges = [];
  let inicio = 0;
  for (let i = 1; i <= columnas.length; i++) {
    if (i === columnas.length || columnas[i].grupo.key !== columnas[inicio].grupo.key) {
      if (i - 1 > inicio) merges.push({ s: { r: 0, c: inicio }, e: { r: 0, c: i - 1 } });
      inicio = i;
    }
  }
  ws["!merges"] = merges;

  columnas.forEach(({ grupo }, idx) => {
    const cellGrupo = XLSX.utils.encode_cell({ r: 0, c: idx });
    const cellCol = XLSX.utils.encode_cell({ r: 1, c: idx });
    if (ws[cellGrupo]) ws[cellGrupo].s = estiloBannerGrupo(grupo.color);
    if (ws[cellCol]) ws[cellCol].s = estiloEncabezadoColumna(grupo.color);
  });
  ws["!cols"] = filaColumnas.map((h) => ({ wch: Math.max(h.length + 2, 15) }));
  ws["!rows"] = [{ hpt: 22 }, { hpt: 26 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja);

  const wsInstr = XLSX.utils.aoa_to_sheet(instrucciones.map((t) => [t]));
  wsInstr["!cols"] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(wb, wsInstr, "Instrucciones");

  const aoaEjemplos = [["Grupo", "Columna", "Ejemplo 1", "Ejemplo 2", "Ejemplo 3"]];
  columnas.forEach(({ col, grupo }) => {
    const ej = ejemplos[col] || [];
    aoaEjemplos.push([grupo.label, col, ej[0] || "", ej[1] || "", ej[2] || ""]);
  });
  const wsEjemplos = XLSX.utils.aoa_to_sheet(aoaEjemplos);
  wsEjemplos["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 32 }, { wch: 32 }, { wch: 32 }];
  for (let c = 0; c < 5; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (wsEjemplos[cellRef]) wsEjemplos[cellRef].s = estiloBannerGrupo("595959");
  }
  columnas.forEach(({ grupo }, idx) => {
    const cellGrupo = XLSX.utils.encode_cell({ r: idx + 1, c: 0 });
    if (wsEjemplos[cellGrupo]) {
      wsEjemplos[cellGrupo].s = {
        fill: { fgColor: { rgb: aclararColor(grupo.color, 0.75) } },
        font: { bold: true, sz: 10, name: "Arial" },
      };
    }
  });
  XLSX.utils.book_append_sheet(wb, wsEjemplos, "Ejemplos");

  catalogos
    .filter((cat) => cat.filas.length)
    .forEach((cat) => {
      const wsCat = XLSX.utils.aoa_to_sheet([cat.encabezados, ...cat.filas]);
      wsCat["!cols"] = cat.encabezados.map(() => ({ wch: 45 }));
      // Excel limita el nombre de la hoja a 31 caracteres.
      XLSX.utils.book_append_sheet(wb, wsCat, cat.nombre.substring(0, 31));
    });

  XLSX.writeFile(wb, nombreArchivo);
};

export const descargarLibroErrores = (filas, nombreArchivo) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), "Errores");
  XLSX.writeFile(wb, nombreArchivo);
};
